/**
 * native javascript osztaly az akadalymentes select létrehozására.
 * ES6 !
 * params: bemeneti paraméterek:
 * id: a sekect tag id-je.
 *
 * @author Erik on 2017.11.24.
 */
import LiveRegion from 'live-region';
import delegate from 'delegate';

import open from './open';
import select from './select';
import arrow from './arrow-keys';
import activate from './activate';
import makeId from './make-id';

export default class NativeAccessibleSelect {
	constructor(params) {
		let element = params.element;
		if (element.getAttribute('has-accessible-select')) {
			return;
		}
		let options = element.querySelectorAll('option');
		let optionString = '';
		let multipleLine = '';
		this.pseudoValue = '';
		this.selectedId = null;
		this.nativeSelect = element;
		for (let optionItem in options) {
			if (options[optionItem].localName == 'option') {
				let item = document.createElement('li');
				item.setAttribute('role', 'option');
				item.classList.add('accessible-option');
				item.id = makeId();
				item.setAttribute('value', options[optionItem].value);
				if (options[optionItem].selected) {
					this.selectedId = item.id;
					let selectedSubtitle = options[optionItem].getAttribute('data-subtitle');
					if (selectedSubtitle) {
						let selectedDiv = document.createElement('div');
						selectedDiv.innerHTML = options[optionItem].innerHTML;
						let selectedSubtitleSpan = document.createElement('span');
						selectedSubtitleSpan.classList.add('subtitle');
						selectedSubtitleSpan.innerHTML = selectedSubtitle;
						selectedDiv.appendChild(selectedSubtitleSpan);
						this.pseudoValue = selectedDiv.innerHTML;
					} else {
						this.pseudoValue = options[optionItem].innerHTML;
					}
					item.classList.add('accessible-option-active');
					item.setAttribute('aria-selected', 'true');
				}
				item.innerHTML = options[optionItem].innerHTML;
				let subtitle = options[optionItem].getAttribute('data-subtitle');
				if (subtitle) {
					let subtitleSpan = document.createElement('span');
					subtitleSpan.classList.add('subtitle');
					subtitleSpan.innerHTML = subtitle;
					item.appendChild(subtitleSpan);
				}
				optionString += item.outerHTML;
				multipleLine = subtitle ? ' has-subtitle' : '';
			}
		}
		let template = `<div class="accessible-select${multipleLine}">
				<div class="accessible-combobox" role="combobox" tabindex="0" 
					aria-autocomplete="none" aria-owns="${element.id}-list" aria-expanded="false" aria-labelledby="${element.id}-label" 
					aria-required="true" aria-activedescendant="${this.selectedId}">
					<div class="accessible-pseudo-value">${this.pseudoValue}</div>
				</div>
				<ul class="accessible-listbox" role="listbox" id="${element.id}-list">
					${optionString}
				</ul>
			</div>
			<div id="accessible-select-layer-${element.id}" class="accessible-select-layer"></div>`;

		this.templateElement = document.createElement('div');
		this.templateElement.classList.add('accessible-select-wrapper');
		this.templateElement.innerHTML = template;
		element.parentElement.appendChild(this.templateElement);

		let combobox = this.templateElement.querySelector('.accessible-combobox');
		let listbox = this.templateElement.querySelector('.accessible-listbox');
		this.combobox = combobox;
		this.listbox = listbox;
		this.coverLayer = `#accessible-select-layer-${element.id}`;
		element.setAttribute('has-accessible-select', true);
		this.attachComboBoxClick(combobox, listbox);
		this.attachComboBoxKeyEvent(combobox, listbox);
		this.attachListboxClickEvent(combobox, listbox, this.coverLayer);
		this.attachLayerClick(this.coverLayer, combobox, listbox);
		this.attachLabelClick(element.id, this.combobox);
	}
	static get liveRegion() {
		return new LiveRegion({
			ariaLive: 'polite'
		});
	}
	attachLabelClick(id, combobox) {
		let label = document.querySelector(`label[for="${id}"]`);
		if (label) {
			label.addEventListener('click', () => {
				if (window.matchMedia('(min-width: 768px)').matches) {
					combobox.focus();
				}
			});
		}
	}
	// combobox events

	attachComboBoxClick(combobox, listbox) {
		combobox.addEventListener('click', () => {
			let layer = document.querySelector(this.coverLayer);
			layer.style.display = 'block';
			listbox.classList.toggle('accessible-listbox-show');
			const hasShowClass = listbox.classList.contains('accessible-listbox-show');
			combobox.setAttribute('aria-expanded', hasShowClass ? 'true' : 'false');
			if (hasShowClass) {
				open(combobox, listbox);
			}
		});
	}

	attachComboBoxKeyEvent(combobox, listbox) {
		combobox.addEventListener('keydown', e => {
			const which = e.which;
			switch (which) {
				case 38: // up
				case 40: // down
					e.preventDefault();
					if (listbox.classList.contains('accessible-listbox-show')) {
						arrow({
							key: which,
							combobox: combobox,
							listbox: listbox,
							liveRegion: this.liveRegion
						});
					} else {
						open(combobox, listbox);
					}
					break;
				case 13: // enter
				case 32: // space
					e.preventDefault();
					if (listbox.classList.contains('accessible-listbox-show')) {
						select(combobox, listbox, this.nativeSelect);
					} else {
						open(combobox, listbox);
					}
					break;
				case 27: // escape
					if (listbox.classList.contains('accessible-listbox-show')) {
						// restore previously selected
						const cachedSelected = listbox.getAttribute('data-cached-selected');
						combobox.setAttribute('aria-activedescendant', cachedSelected);
						listbox.classList.remove('accessible-listbox-show');
						combobox.setAttribute('aria-expanded', 'false');
					}
					break;
				default:
					if (NativeAccessibleSelect.isLetterOrNum(which)) {
						open(combobox, listbox);
					}
			}
		});
	}
	// listbox events

	attachListboxClickEvent(combobox, listbox, layer) {
		function onMouseUp() {
			combobox.focus();
			listbox.classList.remove('accessible-listbox-show');
			combobox.setAttribute('aria-expanded', 'false');
			document.removeEventListener('mouseup', onMouseUp);
			document.querySelector(layer).style.display = 'none';
		}

		delegate(listbox, '[role="option"]', 'mousedown', e => {
			const option = e.delegateTarget;
			// detach blur events so the list doesn't close
			combobox.removeEventListener('blur', this.onComboBlur);
			combobox.setAttribute('aria-activedescendant', option.id);

			activate(combobox, listbox, true);
			select(combobox, listbox, this.nativeSelect, true);

			document.removeEventListener('mouseup', onMouseUp);
			document.addEventListener('mouseup', onMouseUp);
		});
	}
	// layer event

	onLayerClick() {
		document.querySelector(this.coverLayer).style.display = 'none';
		const wasVisible = this.listbox.classList.contains('accessible-listbox-show');

		this.listbox.classList.remove('accessible-listbox-show');
		this.combobox.setAttribute('aria-expanded', 'false');

		const cached = this.listbox.getAttribute('data-cached-selected');
		if (cached && wasVisible) {
			this.combobox.getAttribute('aria-activedescendant', cached);
		}
	}

	attachLayerClick(layer) {
		document.querySelector(layer).addEventListener('click', this.onLayerClick.bind(this));
	}

	static isLetterOrNum(key) {
		var isLetter = key >= 65 && key <= 90;
		var isNumber = key >= 48 && key <= 57;
		return isLetter || isNumber;
	}
}
